/**
 * Isometric drawing primitives — the only way volumes enter the game.
 *
 * Every primitive here obeys Law L1: the caller supplies an anchor, the base of
 * the volume is centred on that anchor, and geometry is built UPWARD. Nothing
 * hangs down from the anchor. (The prototype bug in `art-test-03-yard.html`,
 * where boxes treated the anchor as the top face, is not expressible with these
 * functions — which is the point of having them.)
 *
 * Nothing here knows what a tower or a smelter is. Entities are compositions.
 */

import { TH, TW } from '../render/iso.ts';
import { OUTLINE, type VolumeRamp } from '../render/palette.ts';
import { PixelSurface, type Blend, type Color } from '../render/surface.ts';

/** A sprite in progress, plus the position of its ground anchor inside it. */
export interface EntityCanvas {
  s: PixelSurface;
  /** Anchor position within `s`. Always integers. */
  ax: number;
  ay: number;
}

export interface Sprite {
  surface: PixelSurface;
  /** Anchor position within `surface`. */
  ax: number;
  ay: number;
  /** Footprint in tiles (square). */
  n: number;
  /** Draw operations spent building it — feeds `perfBudget`. */
  ops: number;
}

/**
 * Allocate a canvas big enough for a footprint of `n` tiles and `height` px of
 * volume, with room for the 1 px outline and the overhang Law L2 allows.
 * Padding is generous on purpose: a sprite clipped by its own canvas would
 * produce a fake `outlineIntegrity` pass.
 */
export function newEntityCanvas(n: number, height: number, pad = 8): EntityCanvas {
  const halfW = TW * n;
  const halfH = TH * n;
  const w = halfW * 2 + pad * 2;
  const h = halfH + height + halfH + pad * 2;
  const s = new PixelSurface(w, h);
  return { s, ax: halfW + pad, ay: pad + height + halfH };
}

export function toSprite(c: EntityCanvas, n: number): Sprite {
  return { surface: c.s, ax: c.ax, ay: c.ay, n, ops: c.s.ops };
}

// ---------------------------------------------------------------------------
// Base shapes
// ---------------------------------------------------------------------------

export interface Span {
  y: number;
  x0: number;
  x1: number;
}

/**
 * A 2:1 diamond with `halfH` rows above its centre. Widths step 4 px per row,
 * which is what makes iso edges read as clean staircases instead of jitter.
 * A footprint of `n` tiles is `halfH = TH * n`; the shape is 4*halfH-2 px at its
 * widest inside a 4*halfH pitch, and neighbours interlock in the 1 px margins.
 */
export function diamondSpansH(cx: number, cy: number, halfH: number): Span[] {
  if (!Number.isInteger(cx) || !Number.isInteger(cy)) throw new Error('diamond centre must be integer');
  if (!Number.isInteger(halfH) || halfH < 1) throw new Error(`bad diamond halfH ${halfH}`);
  const rows = halfH * 2;
  const out: Span[] = [];
  for (let r = 0; r < rows; r++) {
    const k = r < halfH ? r : rows - 1 - r;
    const hw = 2 * k + 1;
    out.push({ y: cy - halfH + r, x0: cx - hw, x1: cx + hw - 1 });
  }
  return out;
}

/** Rows of a 2:1 ellipse of horizontal radius `rx` (round forms: tanks, vents). */
export function ellipseSpans(cx: number, cy: number, rx: number): Span[] {
  const ry = Math.max(1, Math.round(rx / 2));
  const out: Span[] = [];
  for (let dy = -ry; dy <= ry; dy++) {
    const t = (dy + 0.5) / (ry + 0.5);
    const hw = Math.round(rx * Math.sqrt(Math.max(0, 1 - t * t)));
    if (hw < 1) continue;
    out.push({ y: cy + dy, x0: cx - hw, x1: cx + hw - 1 });
  }
  return out;
}

export function fillSpans(s: PixelSurface, spans: Span[], color: Color, blend: Blend = 'over'): void {
  for (const sp of spans) s.hline(sp.x0, sp.x1, sp.y, color, blend);
}

/** Filled 2:1 diamond, centred on (cx, cy). */
export function isoDiamond(
  s: PixelSurface,
  cx: number,
  cy: number,
  halfH: number,
  color: Color,
  blend: Blend = 'over',
): void {
  fillSpans(s, diamondSpansH(cx, cy, halfH), color, blend);
}

/** Filled 2:1 ellipse — lamps, vents, contact shadows. */
export function isoDisc(
  s: PixelSurface,
  cx: number,
  cy: number,
  rx: number,
  color: Color,
  blend: Blend = 'over',
): void {
  fillSpans(s, ellipseSpans(cx, cy, rx), color, blend);
}

/** 1 px rim following a diamond's outline. `dashed` skips every other pixel. */
export function isoDiamondRim(
  s: PixelSurface,
  cx: number,
  cy: number,
  halfH: number,
  color: Color,
  dashed = false,
  phase = 0,
): void {
  for (const sp of diamondSpansH(cx, cy, halfH)) {
    for (const x of [sp.x0, sp.x0 + 1, sp.x1 - 1, sp.x1]) {
      if (dashed && ((x + sp.y + phase) & 1) === 0) continue;
      s.px(x, sp.y, color);
    }
  }
}

/**
 * Lowest y of each column of a shape, i.e. where its wall meets the ground.
 * Walls are then one vertical run per column — no column is ever drawn twice,
 * which is what keeps a 2x2 structure inside the 1200-op `perfBudget`.
 */
function columnBottoms(spans: Span[]): Map<number, number> {
  const m = new Map<number, number>();
  for (const sp of spans) {
    for (let x = sp.x0; x <= sp.x1; x++) {
      const prev = m.get(x);
      if (prev === undefined || sp.y > prev) m.set(x, sp.y);
    }
  }
  return m;
}

// ---------------------------------------------------------------------------
// Volumes
// ---------------------------------------------------------------------------

export interface PrismOptions {
  /** Footprint in tiles; the base diamond is this size minus `inset`. */
  n: number;
  /** Rows shaved off the base diamond's half-height (each row = 2 px narrower per side). */
  inset?: number;
  /** Height above the base, in internal px. */
  height: number;
  /** Base offset above the anchor (stacking, plinths). */
  lift?: number;
  ramp: VolumeRamp;
  /** Highlight the lit (north-west) top edge. Default true. */
  litEdge?: boolean;
  /**
   * Screen offset from the anchor for off-centre masses. Keep `dx === 2 * dy`
   * (or its negation) and the mass stays on the iso grid; anything else reads
   * as a building that slid off its tile.
   */
  dx?: number;
  dy?: number;
}

/**
 * Extruded iso volume on a square footprint, built upward from the anchor.
 * Returns the screen y of the top face centre so callers can stack on it.
 */
export function isoPrism(c: EntityCanvas, o: PrismOptions): number {
  const inset = o.inset ?? 0;
  const halfH = TH * o.n - inset;
  if (halfH < 1) throw new Error(`prism inset ${inset} collapses footprint ${o.n}`);
  const cx = c.ax + (o.dx ?? 0);
  const baseY = c.ay - (o.lift ?? 0) + (o.dy ?? 0);
  const topY = baseY - o.height;

  const spans = diamondSpansH(cx, baseY, halfH);
  const bottoms = columnBottoms(spans);
  const xs = [...bottoms.keys()].sort((a, b) => a - b);
  const xMin = xs[0]!;
  const xMax = xs[xs.length - 1]!;

  for (const x of xs) {
    const yb = bottoms.get(x)!;
    let col: Color;
    if (x === cx - 1 || x === cx) col = o.ramp.deep; // near vertical corner, 2 px seam
    else if (x === xMin) col = o.ramp.edge; // lit outer edge
    else if (x === xMax) col = o.ramp.deep;
    else col = x < cx ? o.ramp.left : o.ramp.right;
    if (o.height > 0) c.s.vline(x, yb - o.height, yb, col);
  }

  const topSpans = diamondSpansH(cx, topY, halfH);
  fillSpans(c.s, topSpans, o.ramp.top);
  if (o.litEdge !== false) {
    for (let r = 0; r < halfH; r++) {
      const sp = topSpans[r]!;
      c.s.px(sp.x0, sp.y, o.ramp.edge);
      c.s.px(sp.x0 + 1, sp.y, o.ramp.edge);
    }
  }
  return topY;
}

export interface CylinderOptions {
  /** Horizontal radius in px; the base ellipse is 2:1, so ry = rx / 2. */
  rx: number;
  height: number;
  lift?: number;
  /** Offset from the anchor, for cylinders that are not centred on the footprint. */
  dx?: number;
  dy?: number;
  ramp: VolumeRamp;
  /** Draw the top face as a lit ellipse (false for open tanks and chimneys). */
  cap?: boolean;
}

/** Iso cylinder: elliptical base, curved shading, elliptical cap. Built upward. */
export function isoCylinder(c: EntityCanvas, o: CylinderOptions): number {
  const cx = c.ax + (o.dx ?? 0);
  const baseY = c.ay - (o.lift ?? 0) + (o.dy ?? 0);
  const topY = baseY - o.height;
  const bottoms = columnBottoms(ellipseSpans(cx, baseY, o.rx));

  for (const [x, yb] of bottoms) {
    const t = (x - cx + o.rx) / (2 * o.rx); // 0 at left edge, 1 at right
    const col =
      t < 0.1 ? o.ramp.edge : t < 0.34 ? o.ramp.top : t < 0.72 ? o.ramp.left : o.ramp.right;
    if (o.height > 0) c.s.vline(x, yb - o.height, yb, col);
  }

  if (o.cap !== false) {
    const capSpans = ellipseSpans(cx, topY, o.rx);
    fillSpans(c.s, capSpans, o.ramp.top);
    for (let i = 0; i < Math.max(1, capSpans.length >> 1); i++) {
      const sp = capSpans[i]!;
      c.s.px(sp.x0, sp.y, o.ramp.edge);
    }
  }
  return topY;
}

/**
 * Integer line (Bresenham), `width` px wide horizontally. Struts, legs,
 * harpoon barrels, chain-lightning segments — anything not axis-aligned.
 */
export function linePx(
  s: PixelSurface,
  x0: number,
  y0: number,
  x1: number,
  y1: number,
  color: Color,
  width = 1,
): void {
  let x = x0;
  let y = y0;
  const dx = Math.abs(x1 - x0);
  const dy = -Math.abs(y1 - y0);
  const sx = x0 < x1 ? 1 : -1;
  const sy = y0 < y1 ? 1 : -1;
  let err = dx + dy;
  for (;;) {
    s.hline(x - (width >> 1), x - (width >> 1) + width - 1, y, color);
    if (x === x1 && y === y1) break;
    const e2 = 2 * err;
    if (e2 >= dy) {
      err += dy;
      x += sx;
    }
    if (e2 <= dx) {
      err += dx;
      y += sy;
    }
  }
}

/** A thin vertical post (masts, barrels, antennae). Built upward from `lift`. */
export function post(
  c: EntityCanvas,
  dx: number,
  height: number,
  width: number,
  color: Color,
  lift = 0,
): number {
  const x = c.ax + dx - (width >> 1);
  const baseY = c.ay - lift;
  c.s.fillRect(x, baseY - height, width, height, color);
  return baseY - height;
}

// ---------------------------------------------------------------------------
// Law L6 — the contact outline
// ---------------------------------------------------------------------------

/**
 * The 1 px dark contact outline (doc 10 rule 1, Law L6).
 *
 * Doc 10 describes this as "draw the sprite 4x at +-1 px offsets in near-black,
 * then once for real". Dilating the finished alpha mask produces the identical
 * outer outline for a fraction of the cost, cannot leave a gap, and never
 * stains the sprite's interior — so that is what ships. The rule is unchanged;
 * only the implementation is cheaper.
 */
export function applyOutline(s: PixelSurface, color: Color = OUTLINE): PixelSurface {
  const { width: w, height: h } = s;
  const before = s.ops;
  const mask = s.alphaMask();
  const targets: number[] = [];
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      if (mask[y * w + x]) continue;
      const up = y > 0 && mask[(y - 1) * w + x];
      const dn = y < h - 1 && mask[(y + 1) * w + x];
      const lf = x > 0 && mask[y * w + x - 1];
      const rt = x < w - 1 && mask[y * w + x + 1];
      if (up || dn || lf || rt) targets.push(y * w + x);
    }
  }
  for (const i of targets) s.px(i % w, (i / w) | 0, color, 'set');
  // One whole-surface pass, so it costs the perf budget one op, not one per pixel.
  s.ops = before + 1;
  return s;
}

// ---------------------------------------------------------------------------
// Derived state transforms (doc 10 interaction table)
// ---------------------------------------------------------------------------

/** Ordered 50% checkerboard — the only dither pattern in the game. */
export function ditherOverlay(s: PixelSurface, color: Color, parity = 0): void {
  const before = s.ops;
  for (let y = 0; y < s.height; y++) {
    for (let x = 0; x < s.width; x++) {
      if (((x + y) & 1) !== parity) continue;
      if (s.alphaAt(x, y) === 0) continue;
      s.px(x, y, color);
    }
  }
  s.ops = before + 1; // one whole-surface pass
}

/** Uniformly scale alpha — placement ghosts (60%) and stepped fades. */
export function multiplyAlpha(s: PixelSurface, factor: number): void {
  for (let i = 3; i < s.data.length; i += 4) s.data[i] = s.data[i]! * factor;
}

/** Every non-transparent pixel replaced by one colour (hit flash, black test). */
export function silhouette(s: PixelSurface, color: Color): PixelSurface {
  const out = new PixelSurface(s.width, s.height);
  for (let y = 0; y < s.height; y++) {
    for (let x = 0; x < s.width; x++) {
      if (s.alphaAt(x, y) === 0) continue;
      out.px(x, y, color, 'set');
    }
  }
  return out.resetOps();
}

/** Recolour existing outline pixels — hover swaps black for cyan (doc 10). */
export function recolorOutline(s: PixelSurface, from: Color, to: Color): void {
  for (let y = 0; y < s.height; y++) {
    for (let x = 0; x < s.width; x++) {
      if (s.get(x, y) === from) s.px(x, y, to, 'set');
    }
  }
}
